/**
 * BmiDial.tsx — the 18.5–40 BMI gauge with the patient's marker.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the eligible result screen shows where the patient's BMI sits on the
 * clinically-considered range. Display-only (the server's BMI is authoritative);
 * the marker clamps so extreme values stay on the arc. A hand-rolled SVG
 * semicircle (no chart library), themed via CSS variables. Copy is
 * category-level (R28) — the dial explains a range, not a product.
 *
 * THE BANDS (2026-09-26, Ashley Haveloch-Jones via the CTO: "Indicate in the
 * scale where the BMI is high… if I see this as a novice it doesn't tell me what
 * range of BMI is high"). The arc is split into the NHS adult bands — healthy
 * weight 18.5 to 24.9, overweight 25 to 29.9, obesity 30 and over — in green,
 * amber and red, and the band names sit under the dial in WORDS, with the
 * patient's own band marked, so colour is never the only carrier (WCAG 1.4.1).
 *   - ⚠️ FACTUAL NAMES, NEVER ALARM. The bands are the NHS's own categories and
 *     the labels say only which one a number falls in — never "danger", "at
 *     risk" or anything that would read as a diagnosis or a scare. The number is
 *     still the patient's own and still the server's.
 *   - ⚠️ THE STANDARD BANDS, NOT THE ETHNICITY-ADJUSTED ONES. NICE lowers the
 *     thresholds for some family backgrounds (the eligibility gate already
 *     does, server-side). This dial does not know the patient's background and
 *     must not guess it, so it draws the standard adult bands; the eligibility
 *     verdict above it is the answer that accounts for background.
 *
 * On mount the coloured arc SWEEPS into place — a white stroke inside an SVG
 * `<mask>` (stroke-dashoffset, `pathLength=1` normalised so the maths is
 * arc-length-agnostic) uncovers the three bands — and the marker pops in, the
 * same technique as {@link ./ProjectionChart}'s reveal, on the shared
 * `usePrefersReducedMotion` gate (E3 #8). Reduced-motion users (and non-browser
 * test runners without `matchMedia`) get the finished dial on first paint. The
 * pale band tints under it are the track, so the whole scale reads before the
 * sweep arrives.
 */
import { useId } from 'react';
import { usePrefersReducedMotion, useRevealAfterMount } from '../ui/motion';

const BMI_MIN = 18.5;
const BMI_MAX = 40;

const CX = 140;
const CY = 128;
const R = 110;
const STROKE = 18;

export type BmiBandKey = 'healthy' | 'overweight' | 'obesity';

/** The NHS adult BMI bands, lowest first. `to` is the next band's `from`. */
export const BMI_BANDS: ReadonlyArray<{ key: BmiBandKey; label: string; range: string; from: number; to: number; color: string }> = [
  { key: 'healthy', label: 'Healthy weight', range: '18.5–24.9', from: BMI_MIN, to: 25, color: 'var(--gps-bmi-healthy)' },
  { key: 'overweight', label: 'Overweight', range: '25–29.9', from: 25, to: 30, color: 'var(--gps-bmi-overweight)' },
  { key: 'obesity', label: 'Obesity', range: '30 and over', from: 30, to: BMI_MAX, color: 'var(--gps-bmi-obesity)' },
];

/**
 * Which band a BMI falls in. Below 18.5 is off this dial's scale (it starts at
 * the healthy band) and reads as `null`, never as "healthy".
 */
export function bmiBandFor(bmi: number): (typeof BMI_BANDS)[number] | null {
  if (!Number.isFinite(bmi) || bmi < BMI_MIN) return null;
  if (bmi < 25) return BMI_BANDS[0];
  if (bmi < 30) return BMI_BANDS[1];
  return BMI_BANDS[2];
}

const fractionOf = (value: number) => (value - BMI_MIN) / (BMI_MAX - BMI_MIN);

/** Point on the upper semicircle at fraction `f` (0 = left/18.5, 1 = right/40). */
function pointAt(f: number): { x: number; y: number } {
  const theta = Math.PI - f * Math.PI; // 180° → 0°
  return { x: CX + R * Math.cos(theta), y: CY - R * Math.sin(theta) };
}

/** The arc between two fractions, drawn left to right. */
function arcBetween(from: number, to: number): string {
  const a = pointAt(from);
  const b = pointAt(to);
  return `M ${a.x} ${a.y} A ${R} ${R} 0 0 1 ${b.x} ${b.y}`;
}

/** Semicircular BMI gauge with a positioned marker for the given BMI. */
export function BmiDial({ bmi }: { bmi: number }) {
  const reduced = usePrefersReducedMotion();
  // Flipped a frame after mount so the CSS transition on the initial (empty) arc
  // + hidden marker actually plays; `bmi` restarts it for a new value.
  const revealed = useRevealAfterMount(reduced, bmi);
  // An SVG id, and React's ids carry colons a `url(#…)` reference can trip on.
  const maskId = `bmi-sweep-${useId().replace(/:/g, '')}`;

  const fraction = Math.max(0.03, Math.min(0.97, fractionOf(bmi)));
  const marker = pointAt(fraction);
  const band = bmiBandFor(bmi);

  const swept = arcBetween(0, fraction);
  const start = pointAt(0);
  const end = pointAt(1);

  return (
    <div style={{ display: 'grid', placeItems: 'center', margin: 'var(--gps-s-2) 0' }}>
      <svg
        viewBox="0 0 280 150"
        style={{ width: '100%', maxWidth: '280px' }}
        role="img"
        aria-label={
          band
            ? `Your BMI is ${bmi.toFixed(1)}, in the ${band.label.toLowerCase()} range (${band.range}), shown on a scale from ${BMI_MIN} to ${BMI_MAX}`
            : `Your BMI is ${bmi.toFixed(1)}, shown on a scale from ${BMI_MIN} to ${BMI_MAX}`
        }
      >
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="280" height="150">
            {/* `pathLength={1}` normalises the arc so dasharray/offset are 0..1 and the
                sweep maths never needs the real (curved) length. */}
            <path
              data-dial="sweep"
              d={swept}
              fill="none"
              stroke="#fff"
              pathLength={1}
              strokeWidth={STROKE + 4}
              strokeLinecap="round"
              style={{
                strokeDasharray: 1,
                strokeDashoffset: revealed ? 0 : 1,
                transition: reduced ? 'none' : `stroke-dashoffset var(--gps-dur) var(--gps-ease)`,
              }}
            />
          </mask>
        </defs>

        {/* THE TRACK: every band, pale, so the scale reads before the sweep lands. */}
        <g data-dial="track" opacity="0.22">
          {BMI_BANDS.map((b) => (
            <path key={b.key} d={arcBetween(fractionOf(b.from), fractionOf(b.to))} fill="none" style={{ stroke: b.color }} strokeWidth={STROKE} />
          ))}
          <circle cx={start.x} cy={start.y} r={STROKE / 2} style={{ fill: BMI_BANDS[0].color }} />
          <circle cx={end.x} cy={end.y} r={STROKE / 2} style={{ fill: BMI_BANDS[2].color }} />
        </g>

        {/* THE FILL: the same bands at full strength, uncovered up to the marker. */}
        <g data-dial="fill" mask={`url(#${maskId})`}>
          {BMI_BANDS.map((b) => (
            <path key={b.key} d={arcBetween(fractionOf(b.from), fractionOf(b.to))} fill="none" style={{ stroke: b.color }} strokeWidth={STROKE} />
          ))}
          <circle cx={start.x} cy={start.y} r={STROKE / 2} style={{ fill: BMI_BANDS[0].color }} />
        </g>

        <circle
          cx={marker.x}
          cy={marker.y}
          // The marker "pops" into place as the arc arrives (SVG `r` is a
          // CSS-animatable geometry property in modern browsers). Dark ink with a
          // white ring, so it reads on every band — the old terracotta marker
          // would vanish on the red one.
          r={revealed ? 9 : 0}
          style={{ fill: 'var(--gps-text)', transition: reduced ? 'none' : `r var(--gps-dur) var(--gps-ease)` }}
          stroke="#fff"
          strokeWidth="3"
        />
        {/* The single, precise BMI figure lives here in the dial centre — the
            authoritative number the arc + marker point to. It used to also
            repeat (rounded) below the SVG, which read as two different values
            ("33" vs "32.7"); one precise figure removes that. */}
        <text x={CX} y={CY - 12} textAnchor="middle" fontSize="32" style={{ fill: 'var(--gps-text)', fontFamily: 'var(--gps-font-display)' }}>
          {bmi.toFixed(1)}
        </text>
        <text x={CX} y={CY + 7} textAnchor="middle" fontSize="12" style={{ fill: 'var(--gps-muted)' }}>
          Your BMI
        </text>
        <text x={CX - R} y={CY + 20} textAnchor="middle" fontSize="11" style={{ fill: 'var(--gps-muted)' }}>
          {BMI_MIN}
        </text>
        <text x={CX + R} y={CY + 20} textAnchor="middle" fontSize="11" style={{ fill: 'var(--gps-muted)' }}>
          {BMI_MAX}
        </text>
      </svg>

      {/* THE BANDS IN WORDS — the legend a novice needs, and the reason colour is
          never the only carrier. `aria-hidden`: the dial's own accessible name
          already says which band the patient is in, and reading all three again
          would be noise. */}
      <ul className="gps-bmi-legend" aria-hidden="true" style={legendStyle}>
        {BMI_BANDS.map((b) => {
          const here = band?.key === b.key;
          return (
            <li key={b.key} data-band={b.key} data-current={here ? 'true' : undefined} style={legendItemStyle(here)}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: b.color, flex: 'none' }} />
                <span style={{ fontWeight: here ? 700 : 500, color: 'var(--gps-text)' }}>{b.label}</span>
              </span>
              <span style={{ display: 'block', color: 'var(--gps-muted)', fontVariantNumeric: 'tabular-nums' }}>{b.range}</span>
              {here ? <span style={{ display: 'block', fontWeight: 700, color: 'var(--gps-text)' }}>You’re here</span> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const legendStyle = {
  listStyle: 'none',
  margin: '4px 0 0',
  padding: 0,
  width: '100%',
  maxWidth: '320px',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '6px',
  fontSize: '12px',
  lineHeight: 1.35,
  textAlign: 'center',
} as const;

function legendItemStyle(here: boolean) {
  return {
    padding: '6px 4px',
    borderRadius: 'var(--gps-r-md)',
    border: `1px solid ${here ? 'var(--gps-border-strong)' : 'transparent'}`,
    background: here ? 'var(--gps-surface-2)' : 'transparent',
  } as const;
}
